import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { useAtom } from "jotai";
import { clinicAtom, pharmacyAtom } from "../../_helper/constant/constant-atom";

const ReceiptHeader = ({
  formData,
  handleFormInputChange,
  handleSupplierChange,
}) => {
  const [clinic] = useAtom(clinicAtom);
  const [pharmacy] = useAtom(pharmacyAtom);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedInput, setDebouncedInput] = useState("");
  const debounceTimer = useRef(null);

  const fields = [
    { label: "Receipt Date*", field: "receipt_date", type: "date" },
    {
      label: "Purchase Order No",
      field: "purchase_order_number",
      type: "text",
    },
    { label: "Purchase Date*", field: "purchase_date", type: "date" },
    { label: "Invoice No*", field: "invoice_no", type: "text" },
    { label: "Invoice Date*", field: "invoice_date", type: "date" },
    { label: "Challan No", field: "chalan_no", type: "text" },
  ];

  // Debounce logic for supplier search input
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      setDebouncedInput(searchInput);
    }, 300);

    return () => clearTimeout(debounceTimer.current);
  }, [searchInput]);

  // Fetch suppliers based on debounced input
  const { data: suppliers } = useItemsFilter(
    ApiEndPoint.INVENTORY,
    `?action=getVendor&clinic_id=${clinic.id}&pharmacy_id=${pharmacy?.id}&vendor_name=${debouncedInput}`
  );

  return (
    <div className="grid grid-cols-2 gap-6 mb-6">
      {/* Supplier Name Dropdown */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Supplier Name*
        </label>
        <Select
          isClearable
          options={suppliers?.map(({ vendor_name, id }) => ({
            label: vendor_name,
            value: id,
          }))}
          value={
            formData.vendor_id
              ? {
                  label: formData.vendor_id.vendor_name,
                  value: formData.vendor_id.id,
                }
              : null
          }
          onInputChange={(inputValue) => setSearchInput(inputValue)}
          onChange={(selectedOption) =>
            handleSupplierChange(selectedOption || null)
          }
          placeholder="Select supplier"
          className="w-full"
          styles={{
            control: (provided) => ({
              ...provided,
              borderColor: "gray",
              boxShadow: "none",
            }),
          }}
          noOptionsMessage={() =>
            searchInput
              ? `No suppliers found for "${searchInput}"`
              : "Start typing to search"
          }
        />
      </div>

      {/* Form Fields */}
      {fields.map(({ label, field, type }) => (
        <div key={field}>
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
          <input
            type={type}
            className="border border-gray-300 rounded px-4 py-2 w-full focus:border-blue-500 focus:ring focus:ring-blue-300"
            value={formData[field] || ""}
            onChange={(e) => handleFormInputChange(field, e.target.value)}
          />
        </div>
      ))}

      {/* Challan Date Field with Explanation */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Challan Date*
        </label>
        <input
          type="date"
          className="border border-gray-300 rounded px-4 py-2 w-full focus:border-blue-500 focus:ring focus:ring-blue-300"
          value={formData.chalan_date || ""}
          onChange={(e) => handleFormInputChange("chalan_date", e.target.value)}
        />
        <p className="text-xs text-gray-600 mt-1">
          This is the date when goods were shipped from the supplier.
        </p>
      </div>
    </div>
  );
};

export default ReceiptHeader;
